<template>
  <PageLayout :title="$t('admin.title')" :subtitle="$t('admin.subtitle')">
    <ContentCard card-class="mb-30">
      <div class="admin-stats-grid">
        <div v-for="card in statCards" :key="card.key" class="admin-stats-grid__item">
          <div class="admin-stat-icon mr-3"><i :class="card.icon"></i></div>
          <div>
            <div class="admin-stat-value">{{ card.value }}</div>
            <div class="text-muted small">{{ card.label }}</div>
          </div>
        </div>
      </div>
    </ContentCard>

    <ContentCard>
      <h5 class="mb-3">{{ $t('admin.recent_transactions') }}</h5>
      <div v-if="loading" class="state-loading" role="status" aria-live="polite" aria-busy="true">{{ $t('common.loading') }}</div>
      <div v-else-if="recent.length === 0" class="text-center py-4 text-muted">
        <p class="mb-1">{{ $t('admin.no_transactions') }}</p>
        <p class="small mb-0">{{ $t('admin.no_transactions_hint') }}</p>
      </div>
      <div v-else class="table-responsive">
        <table class="table table-sm table-hover mb-0">
          <thead>
            <tr>
              <th>{{ $t('global.user') }}</th>
              <th>{{ $t('global.plan') }}</th>
              <th>{{ $t('global.provider') }}</th>
              <th>{{ $t('global.status') }}</th>
              <th>{{ $t('global.date') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="sub in recent" :key="sub.id">
              <td>{{ sub.user?.email || sub.user_id }}</td>
              <td>{{ sub.plan?.name || '-' }}</td>
              <td>{{ providerLabel(sub.payment_provider) }}</td>
              <td><span class="badge" :class="statusClass(sub.status)">{{ statusLabel(sub.status) }}</span></td>
              <td>{{ formatDate(sub.created_at) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </ContentCard>
  </PageLayout>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import i18nPage from '@/mixins/i18nPage'

export default {
  mixins: [i18nPage],
  computed: {
    ...mapGetters('admin', ['dashboard', 'loading']),
    recent() {
      return this.dashboard?.recent_subscriptions || []
    },
    statCards() {
      const d = this.dashboard || {}
      return [
        { key: 'users', label: this.$t('admin.total_users'), value: d.user_count ?? '-', icon: 'fas fa-users' },
        { key: 'active', label: this.$t('admin.active_subs'), value: d.active_subscriptions ?? '-', icon: 'fas fa-check-circle' },
        { key: 'pending', label: this.$t('admin.pending_subs'), value: d.pending_subscriptions ?? '-', icon: 'fas fa-clock' },
        { key: 'plans', label: this.$t('admin.plan_count'), value: d.plan_count ?? '-', icon: 'fas fa-layer-group' },
      ]
    },
  },
  async created() {
    await this.fetchDashboard()
  },
  methods: {
    ...mapActions('admin', ['fetchDashboard']),
    formatDate(v) {
      if (!v) return '-'
      return new Date(v).toLocaleString(this.intlLocale)
    },
    providerLabel(provider) {
      const map = {
        paypal: this.$t('billing.provider_paypal'),
        pakasir: this.$t('billing.provider_local'),
      }
      return map[provider] || provider || '-'
    },
    statusClass(status) {
      const map = { active: 'badge-success', pending: 'badge-warning', cancelled: 'badge-secondary' }
      return map[status] || 'badge-light'
    },
  },
}
</script>

<style scoped>
.admin-stats-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.25rem 1.5rem;
}

.admin-stats-grid__item {
  display: flex;
  align-items: center;
}

@media (min-width: 992px) {
  .admin-stats-grid {
    grid-template-columns: repeat(4, 1fr);
  }
}

.admin-stat-icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: rgba(92, 107, 192, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--wc-primary);
  font-size: 1.1rem;
}
.admin-stat-value {
  font-size: 1.5rem;
  font-weight: 700;
}
</style>
